import { useState, useEffect } from 'react'

export interface DayRule {
  day_of_week: number
  week_pattern: string
}

interface DayState {
  off: boolean
  patterns: string[] // subset of EVERY_WEEK | WEEK_1..WEEK_5
}

const DAYS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
]

const WEEK_CHIPS = [
  { value: 'EVERY_WEEK', label: 'Every Week' },
  { value: 'WEEK_1', label: '1st' },
  { value: 'WEEK_2', label: '2nd' },
  { value: 'WEEK_3', label: '3rd' },
  { value: 'WEEK_4', label: '4th' },
  { value: 'WEEK_5', label: '5th' },
]

const emptyDayState = (): DayState => ({ off: false, patterns: [] })

/** Converts the flat rule list the backend returns/expects into the
 * per-day UI state, and back again. Kept here (not duplicated per tab)
 * since Global/Employee/Training-Centre/District all need the exact same
 * conversion. */
export function rulesToDayState(rules: DayRule[]): Record<number, DayState> {
  const state: Record<number, DayState> = {}
  for (const d of DAYS) state[d.value] = emptyDayState()
  for (const r of rules) {
    if (!state[r.day_of_week]) state[r.day_of_week] = emptyDayState()
    state[r.day_of_week].off = true
    state[r.day_of_week].patterns.push(r.week_pattern)
  }
  return state
}

export function dayStateToRules(state: Record<number, DayState>): DayRule[] {
  const rules: DayRule[] = []
  for (const [day, s] of Object.entries(state)) {
    if (!s.off || s.patterns.length === 0) continue
    for (const pattern of s.patterns) {
      rules.push({ day_of_week: Number(day), week_pattern: pattern })
    }
  }
  return rules
}

interface WeekOffDayGridProps {
  initialRules: DayRule[]
  onChange: (rules: DayRule[]) => void
  disabled?: boolean
}

/** The core policy-builder control: one row per day, each togglable "off"
 * with an Every-Week / alternate-week pattern picker. Reused identically
 * across the Global, Employee-wise, Training-Centre-wise, and
 * District-wise tabs — none of them re-implement this. */
export default function WeekOffDayGrid({ initialRules, onChange, disabled }: WeekOffDayGridProps) {
  const [state, setState] = useState<Record<number, DayState>>(() => rulesToDayState(initialRules))

  useEffect(() => {
    setState(rulesToDayState(initialRules))
  }, [initialRules])

  const emit = (next: Record<number, DayState>) => {
    setState(next)
    onChange(dayStateToRules(next))
  }

  const toggleOff = (day: number) => {
    const current = state[day] || emptyDayState()
    const next = {
      ...state,
      [day]: current.off
        ? { off: false, patterns: [] }
        : { off: true, patterns: ['EVERY_WEEK'] }, // default to "every week" the moment a day is turned on
    }
    emit(next)
  }

  const togglePattern = (day: number, pattern: string) => {
    const current = state[day] || emptyDayState()
    let patterns: string[]
    if (pattern === 'EVERY_WEEK') {
      patterns = current.patterns.includes('EVERY_WEEK') ? [] : ['EVERY_WEEK']
    } else {
      // Selecting any specific week clears "Every Week" (mutually exclusive)
      const withoutEvery = current.patterns.filter((p) => p !== 'EVERY_WEEK')
      patterns = withoutEvery.includes(pattern)
        ? withoutEvery.filter((p) => p !== pattern)
        : [...withoutEvery, pattern]
    }
    emit({ ...state, [day]: { off: current.off, patterns } })
  }

  const applyPreset = (day: number, weeks: string[]) => {
    emit({ ...state, [day]: { off: true, patterns: weeks } })
  }

  return (
    <div className="space-y-2">
      {DAYS.map((d) => {
        const dayState = state[d.value] || emptyDayState()
        return (
          <div key={d.value} className="border border-neutral-200 rounded-lg p-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={dayState.off}
                onChange={() => toggleOff(d.value)}
                disabled={disabled}
              />
              <span className="font-medium text-neutral-900 w-24">{d.label}</span>
              {dayState.off && (
                <span className="text-xs text-neutral-500">
                  {dayState.patterns.includes('EVERY_WEEK')
                    ? 'Off every week'
                    : dayState.patterns.length > 0
                    ? `Off on: ${dayState.patterns.map((p) => WEEK_CHIPS.find((c) => c.value === p)?.label).join(', ')}`
                    : 'Select a pattern below'}
                </span>
              )}
            </label>

            {dayState.off && (
              <div className="mt-3 ml-7">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {WEEK_CHIPS.map((chip) => (
                    <button
                      key={chip.value}
                      type="button"
                      disabled={disabled}
                      onClick={() => togglePattern(d.value, chip.value)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        dayState.patterns.includes(chip.value)
                          ? 'bg-primary text-white border-primary'
                          : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50'
                      }`}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
                {/* Quick presets for the common alternate-Saturday-style
                    configurations — just fills in the chips above, nothing
                    these buttons do that clicking chips manually couldn't. */}
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="text-neutral-400">Quick presets:</span>
                  <button type="button" disabled={disabled} onClick={() => applyPreset(d.value, ['WEEK_1', 'WEEK_3'])} className="text-primary hover:underline">
                    1st &amp; 3rd
                  </button>
                  <button type="button" disabled={disabled} onClick={() => applyPreset(d.value, ['WEEK_2', 'WEEK_4'])} className="text-primary hover:underline">
                    2nd &amp; 4th
                  </button>
                  <button type="button" disabled={disabled} onClick={() => applyPreset(d.value, ['WEEK_1', 'WEEK_3', 'WEEK_5'])} className="text-primary hover:underline">
                    1st, 3rd &amp; 5th
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
} 